import React, { useMemo, useState } from 'react';
import { apiGet } from '../../api';
import { useInvestorMe } from '../../hooks/useInvestorMe';
import { useAsync } from '../../hooks/useAsync';
import type { FileItem, FolderNode } from '../../types';
import { canPreviewOnline, fileTypeLabel } from '../../utils/fileType';

type TreeResp = {
  accessMode: 'none' | 'all' | 'folders';
  folders: FolderNode[];
  files: FileItem[];
  allowedFolderIds: string[];
};

function buildPathMap(folders: FolderNode[]) {
  const byId = new Map<string, FolderNode>();
  folders.forEach((f) => byId.set(f.id, f));
  const nameFor = (id: string | null) => {
    if (!id) return '';
    return byId.get(id)?.name || '';
  };
  const pathFor = (id: string | null): string => {
    if (!id) return '';
    const parts: string[] = [];
    let cur = byId.get(id) || null;
    while (cur) {
      parts.unshift(cur.name);
      cur = cur.parentId ? byId.get(cur.parentId) || null : null;
    }
    return parts.join(' / ');
  };
  return { byId, nameFor, pathFor };
}

export default function InvestorDataRoom() {
  const { me } = useInvestorMe({ enforceNda: true });
  const { data, loading, error } = useAsync<TreeResp>(() => apiGet('/api/investor/data-room/tree'), []);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);

  const folders = data?.folders || [];
  const files = data?.files || [];

  const { byId } = useMemo(() => buildPathMap(folders), [folders]);

  const children = useMemo(() => {
    const map = new Map<string | null, FolderNode[]>();
    folders.forEach((f) => {
      const k = f.parentId || null;
      map.set(k, [...(map.get(k) || []), f]);
    });
    map.forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
    return map;
  }, [folders]);

  const filesByFolder = useMemo(() => {
    const map = new Map<string, FileItem[]>();
    files.forEach((fi) => {
      map.set(fi.folderId, [...(map.get(fi.folderId) || []), fi]);
    });
    map.forEach((arr) => arr.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    return map;
  }, [files]);

  const renderFolder = (parentId: string | null) => {
    const kids = children.get(parentId) || [];
    if (!kids.length) return null;
    return (
      <ul>
        {kids.map((f) => (
          <li key={f.id}>
            <button onClick={() => setOpenFolderId(f.id)} style={{ fontWeight: openFolderId === f.id ? 800 : 600 }}>
              {f.name}
            </button>
            {renderFolder(f.id)}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) return <div className="card">Loading Data Room…</div>;
  if (error) return <div className="card">Couldn't load Data Room. ({error})</div>;

  if (!data || data.accessMode === 'none' || (me && me.company.dataRoomAccessMode === 'none')) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Data Room</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          Data Room access is not enabled for your organization yet.
        </p>
      </div>
    );
  }

  const folderToShow = openFolderId || folders.find((f) => f.parentId == null)?.id || null;
  const folderFiles = folderToShow ? filesByFolder.get(folderToShow) || [] : [];

  return (
    <div className="grid" style={{ gridTemplateColumns: '360px 1fr', gap: 14 }}>
      <div className="card">
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>Folders</div>
        <div className="tree">{renderFolder(null)}</div>
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 900 }}>Files</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {folderFiles.length} files
          </div>
        </div>
        <div className="hr" />
        {folderToShow && byId.get(folderToShow) ? (
          <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Folder: <span className="pill">{byId.get(folderToShow)!.name}</span>
          </div>
        ) : null}
        {!folderFiles.length ? (
          <div className="muted">No files in this folder.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th className="muted">Uploaded</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {folderFiles.map((f) => (
                <tr key={f.id}>
                  <td>{f.name}</td>
                  <td className="muted">{fileTypeLabel(f.name, f.mimeType)}</td>
                  <td className="muted">{f.createdAt ? f.createdAt.slice(0, 10) : '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canPreviewOnline(f.mimeType) ? (
                      <a className="btn" href={`/file/${f.id}/view`} target="_blank" rel="noopener">
                        Preview online
                      </a>
                    ) : null}{' '}
                    <a className="btn" href={`/file/${f.id}/download`} target="_blank" rel="noreferrer">
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
